import React, { Component } from 'react'
import Button from '@material-ui/core/Button'
import Dialog from './IdentifiedDialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Tooltip from '@material-ui/core/Tooltip'
import { withStyles } from '@material-ui/core/styles'
import CKEditor from 'react-ckeditor-component'
import { ckeditorDocumentConfig, ckeditorScript } from '../utils'
import { processHtmlWithTableBorders, removeLinks } from '../utils/htmlUtils'
import HtmlLinkDialog from './HtmlLinkDialog'

const styles = () => ({
  ckeditor: {
    height: '100%',
  },
  editorToolbar: {
    display: 'flex',
    gap: '8px',
    marginBottom: '10px',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  tabButton: {
    minWidth: '100px',
  },
  sourceTextarea: {
    width: '100%',
    height: '480px',
    fontFamily: 'monospace',
    fontSize: '13px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    padding: '8px',
    resize: 'vertical',
    boxSizing: 'border-box',
    lineHeight: 1.5,
  },
})

const localCkeditorConfig = {
  ...ckeditorDocumentConfig,
  bodyClass: 'ContentPreview ContentPreview-min-600 InformedContentStyle',
}

class LocalHtmlEditorModal extends Component {
  constructor(props) {
    super(props)
    this.state = {
      newHTMLContent: props.initialContent,
      activeTab: 'visual',
      linkDialogOpen: false,
      linkInitial: null,
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.open && nextProps.open !== this.props.open) {
      this.setState({
        newHTMLContent: nextProps.initialContent,
        activeTab: 'visual',
        linkDialogOpen: false,
        linkInitial: null,
      })
    }
  }

  handleEditorChange = (event) => {
    const data = event.editor.getData()
    this.setState({ newHTMLContent: data })
  }

  handleTextareaChange = (e) => {
    this.setState({ newHTMLContent: e.target.value })
  }

  handleRemoveLinks = () => {
    this.setState((prev) => ({
      newHTMLContent: removeLinks(prev.newHTMLContent),
    }))
  }

  handleAddTableBorders = () => {
    this.setState((prev) => ({
      newHTMLContent: processHtmlWithTableBorders(prev.newHTMLContent),
    }))
  }

  handleOpenLink = type => {
    const { activeTab, newHTMLContent } = this.state
    let text = ''
    let url = ''
    let contentId = ''
    this.selectedEditorLink = null

    if (activeTab === 'visual' && this.editor) {
      const selection = this.editor.getSelection()
      const startElement = selection && selection.getStartElement()
      const link =
        startElement && startElement.getName() === 'a'
          ? startElement
          : startElement && startElement.getAscendant('a', true)

      text = (selection && selection.getSelectedText()) || (link && link.getText()) || ''
      url = (link && link.getAttribute('href')) || ''
      contentId = (link && link.getAttribute('data-href')) || ''
      this.selectedEditorLink = link || null
    } else if (this.sourceTextarea) {
      const start = this.sourceTextarea.selectionStart || 0
      const end = this.sourceTextarea.selectionEnd || start
      this.sourceSelection = { start, end }
      text = newHTMLContent.slice(start, end)
    }

    this.setState({
      linkDialogOpen: true,
      linkInitial: {
        type,
        text,
        url: type === 'external' && url !== '#' ? url : '',
        contentId: type === 'internal' ? contentId : '',
      },
    })
  }

  handleApplyLink = html => {
    const { activeTab, newHTMLContent } = this.state

    if (activeTab === 'visual' && this.editor) {
      const selection = this.editor.getSelection()
      if (this.selectedEditorLink && selection) {
        selection.selectElement(this.selectedEditorLink)
      }
      this.editor.insertHtml(html)
      this.setState({
        newHTMLContent: this.editor.getData(),
        linkDialogOpen: false,
      })
      return
    }

    const selection = this.sourceSelection || { start: newHTMLContent.length, end: newHTMLContent.length }
    const nextContent =
      newHTMLContent.slice(0, selection.start) + html + newHTMLContent.slice(selection.end)
    this.setState({ newHTMLContent: nextContent, linkDialogOpen: false })
  }

  render() {
    const {
      open,
      onClose,
      onApply,
      classes,
      modalId = 'editor-html-local',
      modalTitle = 'Editor HTML — Conteúdo',
      modalParams,
    } = this.props
    const { newHTMLContent, activeTab } = this.state

    return (
      <Dialog
        modalId={modalId}
        modalParams={modalParams}
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle id={modalId + '-title'}>{modalTitle}</DialogTitle>

        <DialogContent>
          <div className={classes.editorToolbar}>
            <Button
              className={classes.tabButton}
              size="small"
              variant={activeTab === 'source' ? 'contained' : 'outlined'}
              color="primary"
              onClick={() => this.setState({ activeTab: 'source' })}
            >
              Fonte HTML
            </Button>
            <Button
              className={classes.tabButton}
              size="small"
              variant={activeTab === 'visual' ? 'contained' : 'outlined'}
              color="primary"
              onClick={() => this.setState({ activeTab: 'visual' })}
            >
              Visual
            </Button>

            <div style={{ flex: 1 }} />

            <Button size="small" variant="outlined" onClick={() => this.handleOpenLink('external')}>
              Link externo
            </Button>
            <Button size="small" variant="outlined" onClick={() => this.handleOpenLink('internal')}>
              Link interno do Informed
            </Button>

            <Tooltip title="Remove todas as tags <a> mantendo o texto">
              <Button size="small" variant="outlined" onClick={this.handleRemoveLinks}>
                Remover Links
              </Button>
            </Tooltip>

            <Tooltip title="Adiciona bordas em todas as tabelas e destaca cabeçalhos (gravata)">
              <Button size="small" variant="outlined" onClick={this.handleAddTableBorders}>
                Bordas nas Tabelas
              </Button>
            </Tooltip>
          </div>

          {activeTab === 'source' && (
            <textarea
              ref={element => {
                this.sourceTextarea = element
              }}
              className={classes.sourceTextarea}
              value={newHTMLContent}
              onChange={this.handleTextareaChange}
              spellCheck={false}
            />
          )}

          {activeTab === 'visual' && (
            <div style={{ minHeight: '400px' }}>
              <CKEditor
                key={open ? 'editor-open' : 'editor-closed'}
                content={newHTMLContent}
                config={localCkeditorConfig}
                scriptUrl={ckeditorScript}
                events={{
                  change: this.handleEditorChange,
                  instanceReady: event => {
                    this.editor = event.editor
                  },
                }}
                activeClass={classes.ckeditor}
              />
            </div>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="default">
            Cancelar
          </Button>
          <Button onClick={() => onApply(newHTMLContent)} color="primary" variant="contained">
            Aplicar Alterações
          </Button>
        </DialogActions>

        <HtmlLinkDialog
          modalId={modalId + '-link'}
          open={this.state.linkDialogOpen}
          initial={this.state.linkInitial}
          onClose={() => this.setState({ linkDialogOpen: false })}
          onApply={this.handleApplyLink}
        />
      </Dialog>
    )
  }
}

export default withStyles(styles)(LocalHtmlEditorModal)



// WEBPACK FOOTER //
// ./src/components/LocalHtmlEditorModal.js